import React, { useMemo } from 'react';
import styled from 'styled-components';
import { Drawer, Descriptions, Tag, Table, Button, Space, Typography, Tooltip, message } from 'antd';
import {
  PiClipboardTextBold,
  PiMapTrifoldBold,
  PiCopyBold,
  PiCalendarBlankBold,
  PiCheckCircleBold,
  PiXCircleBold,
  PiNotePencilBold,
} from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import type { CollectForm_Base } from '~/common/typescript/models/CollectForm';
import type { Project_Base } from '~/common/typescript/models/Project';
import { parseFormContent, ParsedFormField } from '~/components/Map/panels/GeocollectDatatablePanel/utils/CollectFormParser';
import { formatDate } from '~/utils/dateUtils';
import Routes from '~/routes/routes';

const { Text } = Typography;

interface CollectFormDetailDrawerProps {
  visible: boolean;
  onClose: () => void;
  form: CollectForm_Base | null;
  projectsMap: Map<number, Project_Base>;
}

export const CollectFormDetailDrawer: React.FC<CollectFormDetailDrawerProps> = ({
  visible,
  onClose,
  form,
  projectsMap,
}) => {
  const navigate = useNavigate();

  const parsedContent = useMemo(() => {
    if (!form) return null;
    return parseFormContent(form);
  }, [form]);

  if (!form) return null;

  const project = projectsMap.get(form.project_id);
  const projectName = project ? project.name : `Dự án #${form.project_id}`;
  const isV2 = form.form_version === 2 || parsedContent?.version === 'v2';

  const handleCopyUUID = () => {
    if (form.uuid) {
      navigator.clipboard.writeText(form.uuid);
      message.success('Đã sao chép UUID vào bộ nhớ tạm');
    }
  };

  const handleOpenMap = () => {
    if (form.project_id) {
      navigate(`${Routes.PROJECT}/${form.project_id}`);
    }
  };

  const fieldColumns = [
    {
      title: 'Mã trường (Key)',
      dataIndex: 'key',
      key: 'key',
      render: (key: string) => <Text code>{key}</Text>,
    },
    {
      title: 'Tên hiển thị (Label)',
      dataIndex: 'label',
      key: 'label',
      render: (label: string) => <span style={{ fontWeight: 500 }}>{label || '-'}</span>,
    },
    {
      title: 'Kiểu dữ liệu',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => (
        <Tag color="geekblue" style={{ textTransform: 'capitalize' }}>
          {type || 'text'}
        </Tag>
      ),
    },
    {
      title: 'Bắt buộc',
      dataIndex: 'required',
      key: 'required',
      width: 100,
      render: (required: boolean) =>
        required ? (
          <Tag color="red" icon={<PiCheckCircleBold style={{ verticalAlign: -1 }} />}>
            Bắt buộc
          </Tag>
        ) : (
          <Tag color="default">Tuỳ chọn</Tag>
        ),
    },
    {
      title: 'Tuỳ chọn / Cấu hình',
      key: 'options',
      render: (_: any, record: ParsedFormField) => {
        if (record.options && record.options.length > 0) {
          return (
            <Space size={4} wrap>
              {record.options.slice(0, 5).map((opt, i) => (
                <Tag key={i}>{typeof opt === 'string' ? opt : opt.label || opt.value}</Tag>
              ))}
              {record.options.length > 5 && (
                <Tag color="default">+{record.options.length - 5}</Tag>
              )}
            </Space>
          );
        }
        return <span style={{ color: '#94a3b8' }}>-</span>;
      },
    },
  ];

  return (
    <Drawer
      title={
        <UI_DrawerTitle>
          <PiClipboardTextBold size={20} color="#2563eb" />
          <span>{form.name || 'Chi tiết biểu mẫu'}</span>
        </UI_DrawerTitle>
      }
      placement="right"
      width={680}
      onClose={onClose}
      open={visible}
      extra={
        <Space>
          <Button
            icon={<PiNotePencilBold size={15} />}
            onClick={() => {
              onClose();
              navigate(`${Routes.COLLECT_FORM_DESIGN}?formUuid=${form.uuid}&projectId=${form.project_id}`);
            }}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
          >
            Thiết kế biểu mẫu
          </Button>
          <Button
            type="primary"
            icon={<PiMapTrifoldBold size={16} />}
            onClick={handleOpenMap}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
          >
            Mở trong bản đồ
          </Button>
        </Space>
      }
    >
      <UI_DrawerBody>
        <Descriptions column={2} bordered size="small">
          <Descriptions.Item label="Tên biểu mẫu" span={2}>
            <span style={{ fontWeight: 600 }}>{form.name}</span>
          </Descriptions.Item>

          <Descriptions.Item label="Dự án" span={2}>
            <Tag color="cyan">{projectName}</Tag>
            <span style={{ fontSize: 12, color: '#64748b' }}>(ID: {form.project_id})</span>
          </Descriptions.Item>

          <Descriptions.Item label="UUID">
            <Space size={4}>
              <Text code style={{ fontSize: 12 }}>
                {form.uuid ? `${form.uuid.substring(0, 13)}...` : '-'}
              </Text>
              <Tooltip title="Sao chép UUID">
                <Button
                  type="text"
                  size="small"
                  icon={<PiCopyBold size={14} />}
                  onClick={handleCopyUUID}
                />
              </Tooltip>
            </Space>
          </Descriptions.Item>

          <Descriptions.Item label="Phiên bản">
            <Tag color={isV2 ? 'blue' : 'orange'}>{isV2 ? 'Phiên bản V2' : 'Phiên bản V1'}</Tag>
          </Descriptions.Item>

          <Descriptions.Item label="Ngày tạo">
            <span style={{ fontSize: 12, color: '#64748b' }}>
              {formatDate(form.created_at)}
            </span>
          </Descriptions.Item>

          <Descriptions.Item label="Cập nhật gần nhất">
            <span style={{ fontSize: 12, color: '#64748b' }}>
              {formatDate(form.updated_at || form.created_at)}
            </span>
          </Descriptions.Item>

          <Descriptions.Item label="Mô tả" span={2}>
            {form.description || <span style={{ color: '#94a3b8' }}>Chưa có mô tả</span>}
          </Descriptions.Item>
        </Descriptions>

        <UI_FieldsSection>
          <UI_SectionHeader>
            <UI_SectionTitle>
              Danh sách trường dữ liệu
              <Tag color="blue" style={{ marginLeft: 8 }}>
                {parsedContent?.fields?.length || 0} trường
              </Tag>
            </UI_SectionTitle>
          </UI_SectionHeader>

          <Table
            dataSource={parsedContent?.fields || []}
            columns={fieldColumns}
            rowKey="key"
            pagination={false}
            size="small"
            bordered
          />
        </UI_FieldsSection>
      </UI_DrawerBody>
    </Drawer>
  );
};

const UI_DrawerTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
`;

const UI_DrawerBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const UI_FieldsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const UI_SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const UI_SectionTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  display: flex;
  align-items: center;
`;
